import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Heart, ChevronRight } from "lucide-react";
import { Card, CardContent } from "./ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PrenomsSection() {
  const navigate = useNavigate();
  const [prenoms, setPrenoms] = useState([]);
  const [randomPrenoms, setRandomPrenoms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPrenoms();
  }, []);

  const fetchPrenoms = async () => {
    try {
      const response = await fetch(`${API}/prenoms`);
      if (response.ok) {
        const data = await response.json();
        setPrenoms(data);
        selectRandomPrenoms(data);
      } else {
        const fallbackPrenoms = [
          { prenom: "Kouassi", genre: "masculin", signification: "Né un dimanche", popularite: 100 },
          { prenom: "Akissi", genre: "feminin", signification: "Née un dimanche", popularite: 96 },
          { prenom: "Kouamé", genre: "masculin", signification: "Né un samedi", popularite: 98 },
          { prenom: "Amoin", genre: "feminin", signification: "Née un samedi", popularite: 90 },
        ];
        setPrenoms(fallbackPrenoms);
        setRandomPrenoms(fallbackPrenoms);
      }
    } catch (error) {
      console.error("Error fetching prenoms:", error);
      const fallbackPrenoms = [
        { prenom: "Kouassi", genre: "masculin", signification: "Né un dimanche", popularite: 100 },
        { prenom: "Akissi", genre: "feminin", signification: "Née un dimanche", popularite: 96 },
        { prenom: "Kouamé", genre: "masculin", signification: "Né un samedi", popularite: 98 },
        { prenom: "Amoin", genre: "feminin", signification: "Née un samedi", popularite: 90 },
      ];
      setPrenoms(fallbackPrenoms);
      setRandomPrenoms(fallbackPrenoms);
    } finally {
      setLoading(false);
    }
  };

  const selectRandomPrenoms = (allPrenoms) => {
    const shuffled = [...allPrenoms].sort(() => 0.5 - Math.random());
    setRandomPrenoms(shuffled.slice(0, 4));
  };

  const getGenreIcon = (genre) => {
    return genre === "masculin" ? "👨" : "👩";
  };

  const getGenreColor = (genre) => {
    return genre === "masculin" 
      ? "bg-blue-500/10 text-blue-600 dark:text-blue-400" 
      : "bg-pink-500/10 text-pink-600 dark:text-pink-400";
  };

  return (
    <section className="max-w-7xl mx-auto px-4 py-16 bg-gradient-to-b from-background to-muted/30">
      {/* Header */}
      <div className="text-center mb-12 animate-fade-in">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-4">
          <Sparkles className="w-4 h-4" />
          <span className="text-sm font-semibold">Prénoms Baoulé</span>
        </div>
        <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
          Découvrez les Prénoms Baoulé
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Chaque prénom baoulé raconte une histoire unique, liée au jour de naissance, 
          au rang dans la famille ou aux circonstances de la venue au monde.
        </p>
      </div>

      {/* 4 Prenoms aléatoires */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto mb-8">
          {Array(4).fill(0).map((_, i) => (
            <Card key={i} className="p-4 animate-pulse">
              <div className="h-6 bg-muted rounded mb-2"></div>
              <div className="h-4 bg-muted rounded w-3/4"></div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto mb-8 animate-fade-in">
          {randomPrenoms.map((prenom, index) => (
            <Card 
              key={prenom.prenom_id || index}
              className="group hover:shadow-lg hover:scale-105 transition-all duration-300 cursor-pointer border-border/50 overflow-hidden"
              onClick={() => navigate('/prenoms')}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-heading text-lg font-bold group-hover:text-primary transition-colors">
                      {prenom.prenom}
                    </h3>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getGenreColor(prenom.genre)}`}>
                      {getGenreIcon(prenom.genre)} {prenom.genre}
                    </span>
                  </div>
                  {prenom.popularite >= 90 && (
                    <Heart className="w-4 h-4 text-red-500 fill-red-500" />
                  )}
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {prenom.signification}
                </p>
                {prenom.origine && (
                  <div className="mt-2 pt-2 border-t border-border/50">
                    <span className="text-xs text-muted-foreground">
                      📚 {prenom.origine}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Bouton Voir Tout */}
      <div className="text-center mb-8">
        <button 
          onClick={() => navigate('/prenoms')}
          className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-all hover:scale-105 shadow-lg"
        >
          Voir tous les {prenoms.length} prénoms
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Info Box */}
      <Card className="max-w-4xl mx-auto bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-heading text-xl font-bold mb-2">
                La Tradition des Prénoms Baoulé
              </h3>
              <p className="text-muted-foreground">
                Dans la culture baoulé, les prénoms sont traditionnellement donnés selon le jour de naissance. 
                Chaque jour de la semaine a son prénom masculin et féminin correspondant. Cette tradition permet 
                de connaître immédiatement le jour de naissance d'une personne simplement en entendant son prénom !
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
